import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface SelectDatesState {
  // Maps horse microchip to arrival date (ISO string format)
  arrivalDates: Record<string, string>;
  // Maps horse microchip to departing date (ISO string format)
  departingDates: Record<string, string>;
  // Default arrival date for all horses (ISO string format)
  defaultArrivalDate: string | null;
  // Default departing date for all horses (ISO string format)
  defaultDepartingDate: string | null;
  // Show start date (ISO string format)
  showsStartDate: string | null;
  // Show end date (ISO string format)
  showsEndDate: string | null;
}

const initialState: SelectDatesState = {
  arrivalDates: {},
  departingDates: {},
  defaultArrivalDate: null,
  defaultDepartingDate: null,
  showsStartDate: null,
  showsEndDate: null,
};

const selectDatesSlice = createSlice({
  name: "selectDates",
  initialState,
  reducers: {
    // Arrival Dates Actions
    setArrivalDate: (state, action: PayloadAction<{ microchip: string; date: string }>) => {
      const { microchip, date } = action.payload;
      state.arrivalDates[microchip] = date;
    },
    setArrivalDates: (state, action: PayloadAction<Record<string, string>>) => {
      state.arrivalDates = action.payload;
    },
    setAllArrivalDatesToDate: (state, action: PayloadAction<string>) => {
      const newDate = action.payload;
      // Update all existing arrival dates to the new date
      Object.keys(state.arrivalDates).forEach((microchip) => {
        state.arrivalDates[microchip] = newDate;
      });
      // Also set the default arrival date for display in header
      state.defaultArrivalDate = newDate;
    },
    clearArrivalDate: (state, action: PayloadAction<string>) => {
      delete state.arrivalDates[action.payload];
    },

    // Departing Dates Actions
    setDepartingDate: (state, action: PayloadAction<{ microchip: string; date: string }>) => {
      const { microchip, date } = action.payload;
      state.departingDates[microchip] = date;
    },
    setDepartingDates: (state, action: PayloadAction<Record<string, string>>) => {
      state.departingDates = action.payload;
    },
    clearDepartingDate: (state, action: PayloadAction<string>) => {
      delete state.departingDates[action.payload];
    },

    // Default Arrival Date Actions
    setDefaultArrivalDate: (state, action: PayloadAction<string | null>) => {
      state.defaultArrivalDate = action.payload;
    },

    // Default Departing Date Actions
    setDefaultDepartingDate: (state, action: PayloadAction<string | null>) => {
      state.defaultDepartingDate = action.payload;
    },

    // Show Dates Actions
    setShowsStartDate: (state, action: PayloadAction<string | null>) => {
      state.showsStartDate = action.payload;
    },
    setShowsEndDate: (state, action: PayloadAction<string | null>) => {
      state.showsEndDate = action.payload;
    },

    // Reset all state
    resetSelectDates: () => initialState,
  },
  selectors: {
    selectArrivalDates: (sliceState) => sliceState.arrivalDates,
    selectDepartingDates: (sliceState) => sliceState.departingDates,
    selectDefaultArrivalDate: (sliceState) => sliceState.defaultArrivalDate,
    selectDefaultDepartingDate: (sliceState) => sliceState.defaultDepartingDate,
    selectShowsStartDate: (sliceState) => sliceState.showsStartDate,
    selectShowsEndDate: (sliceState) => sliceState.showsEndDate,
  },
});

export const {
  setArrivalDate,
  setArrivalDates,
  setAllArrivalDatesToDate,
  clearArrivalDate,
  setDepartingDate,
  setDepartingDates,
  clearDepartingDate,
  setDefaultArrivalDate,
  setDefaultDepartingDate,
  setShowsStartDate,
  setShowsEndDate,
  resetSelectDates,
} = selectDatesSlice.actions;

export const {
  selectArrivalDates,
  selectDepartingDates,
  selectDefaultArrivalDate,
  selectDefaultDepartingDate,
  selectShowsStartDate,
  selectShowsEndDate,
} = selectDatesSlice.selectors;

export default selectDatesSlice.reducer;
